import React from 'react';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useUsers } from 'contexts/AuthContext';

import { formatDateTime } from 'utils/DateTimeFormatter';

import { KnzFrontmatter } from './types';

type Props = {
  frontmatter: KnzFrontmatter;
  /** ファイルの作成日時（API6106 の createdAt） */
  createdAt?: string;
  /** ファイルの更新日時（API6106 の updatedAt） */
  updatedAt?: string;
};

/**
 * frontmatter から記事ヘッダーを自動描画する。
 * author（複数可）は User ストアで表示名を解決し、解決できなければ userId をそのまま表示。
 * 複数著者は「、」で連結する。作成日・更新日はファイル日時。無ければ `-`。
 */
export const KnzArticleHeader: React.FC<Props> = ({ frontmatter, createdAt, updatedAt }) => {
  const users = useUsers();
  const authorLabel = frontmatter.authors
    .map((id) => users.find((u) => u.userId === id)?.userName ?? id)
    .join('、');
  // 副題（任意）。読み物モード以外でもタイトル下に表示する。
  const subtitle = frontmatter.extra.subtitle;

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 1 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: subtitle ? 0.5 : 1 }}>
        {frontmatter.title}
      </Typography>
      {subtitle && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {authorLabel}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            作成: {createdAt ? formatDateTime(createdAt) : '-'} / 更新:{' '}
            {updatedAt ? formatDateTime(updatedAt) : '-'}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default KnzArticleHeader;
